import { useEffect, useState } from 'react'
import Modal from '@mui/material/Modal'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { Grid, Skeleton } from '@mui/material'

import { usePreparation } from '@/hooks/usePreparation'
import {
  IInsertPreparationServiceRequest,
  IUpdateIndividualPreparationServiceRequest,
} from '@/types/preparation.types'

import CustomButton from '@/components/CustomButton'
import CustomGraphy from '@/components/CustomGraphy'
import CustomView from '@/components/CustomView'
import FormSelect from '@/components/FormSelect'
import Loader from '@/components/Loader'
import InputCurrency from '@/components/InputCurrency'

import { newServiceSchema } from '@/utils/yup/schemas/preparation/new/PreparationNewSchema'
import { INews } from '@/types/customTable.types'
import { getParamByParam } from '@/utils/index'
import { getIdByName } from '@/pages/Parametrization/utils'
import { CONSTANTS, SUCCESS_STATUS } from '@/utils/CONSTANTS'

import BreakdownGallery from '../BreakdownGallery'

function NewServicePreparation({ returnToTable }: INews) {
  const {
    ALL_SERVICES_LIST,
    GET_ALL_SERVICES,
    CATEGORY_LIST,
    GET_SERVICES,
    PREPARATION_DATA,
    PREPARATION_SERVICE_DATA,
    GET_BREAKDOWN_PHOTOS,
    UPDATE_PREPARATION_SERVICE,
    CREATE_PREPARATION_SERVICE,
  } = usePreparation()
  const [isLoading, setLoading] = useState(false)
  const { preparationId, inspection } = PREPARATION_DATA
  const { id: inspectionId } = inspection
  const schema = newServiceSchema()
  const isEditing = PREPARATION_SERVICE_DATA != null
  const [isPhotosLoading, setIsPhotosLoading] = useState(true)
  const [images, setImages] = useState([])

  useEffect(() => {
    fetchBreakdownPhotos()
  }, [inspectionId])

  const fetchBreakdownPhotos = async () => {
    try {
      const fetchPhotos = await GET_BREAKDOWN_PHOTOS(inspectionId)
      const formattedPhotos = fetchPhotos.map((section: any) => ({
        ...section,
        itensFoto: section.itensFoto.map((item: any) => {
          return {
            id: item?.id,
            url: item?.foto,
            description: item?.nome ?? '',
            subtitle: item?.observacao ?? '',
          }
        }),
      }))
      setImages(formattedPhotos)
    } finally {
      setIsPhotosLoading(false)
    }
  }

  const { values, handleSubmit, handleChange, errors, touched, setFieldValue, setValues } =
    useFormik({
      initialValues: {
        id: PREPARATION_SERVICE_DATA?.id ?? '',
        category: PREPARATION_SERVICE_DATA?.categoria ?? '',
        service: PREPARATION_SERVICE_DATA?.servico ?? '',
        inspectionPhotoId: PREPARATION_SERVICE_DATA?.vistoriaParametrizacaoFotoId ?? '',
        value: PREPARATION_SERVICE_DATA?.valorOrcado ?? 0,
        observacaoAvaria: PREPARATION_SERVICE_DATA?.observacaoAvaria ?? '',
      },
      enableReinitialize: true,
      validationSchema: schema,
      onSubmit: (values) => {
        onSubmit(values)
      },
    })

  const handleCategoryService = (name: string, value: any) => {
    if (!value) {
      setValues({
        ...values,
        [name]: '',
        service: '',
      })
      return
    }

    setValues({
      ...values,
      [name]: value.nome,
      service: '',
    })
  }

  const handleNameService = (name: string, value: any) => {
    if (!value) return setFieldValue(name, '')

    const findCategory = CATEGORY_LIST?.rows?.find((item: any) => item.id === value.categoriaId)

    setValues({
      ...values,
      [name]: value.nome,
      category: findCategory?.nome,
    })
  }

  useEffect(() => {
    GET_ALL_SERVICES({
      categoriaId: getParamByParam(values.category, CATEGORY_LIST?.rows, 'nome', 'id'),
      ativo: true,
      pageSize: CONSTANTS.allRegisters,
    })
  }, [values.category])

  async function onSubmit(e: any) {
    setLoading(true)
    const payloadInsert: IInsertPreparationServiceRequest = {
      preparacaoId: preparationId,
      servicoId: getIdByName(e.service, ALL_SERVICES_LIST?.rows),
      vistoriaParametrizacaoFotoId: e?.inspectionPhotoId!,
      observacaoAvaria: e?.inspectionPhoto?.subtitle ?? '',
      valorOrcado: Number(e.value),
    }

    const payloadUpdate: IUpdateIndividualPreparationServiceRequest = {
      preparacaoServicoId: e?.id,
      servicoId: getIdByName(e.service, ALL_SERVICES_LIST?.rows),
      observacaoAvaria: e?.inspectionPhoto?.subtitle ?? '',
      vistoriaParametrizacaoFotoId: e?.inspectionPhotoId!,
      valorOrcado: Number(e.value),
    }

    try {
      const response: any = isEditing
        ? await UPDATE_PREPARATION_SERVICE(payloadUpdate)
        : await CREATE_PREPARATION_SERVICE(payloadInsert)

      if (SUCCESS_STATUS.includes(response.status)) {
        await GET_SERVICES({ preparacaoId: preparationId })
        toast.success(`Serviço ${isEditing ? 'editado' : 'adicionado'} com sucesso!`)
      }
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setLoading(false)
      returnToTable()
    }
  }

  const styleModal = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '200px',
    borderRadius: '8px',
    border: '1.5px solid #C4C4C4',
    boxShadow: 24,
    p: 4,
  }

  return (
    <Modal keepMounted open={true}>
      <CustomView backColor="white" sx={styleModal} style={{ minWidth: '800px' }}>
        <CustomView backColor="white" justifyContent="center" p="32px" alignItems="center">
          <CustomView borderBottom="1px solid #8F8E914D">
            <CustomGraphy fontSize="18px" mb="16px">
              {isEditing ? 'Editar' : 'Adicionar'} serviço
            </CustomGraphy>
          </CustomView>
          {isLoading ? (
            <Loader inLoading={isLoading} type={'component'} />
          ) : (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <CustomView
                maxWidth={967}
                mt="36px"
                justifyContent="flex-start"
                gap={1}
                flexDirection={{ xs: 'column', md: 'row' }}
              >
                <FormSelect
                  withBorder={true}
                  label="Nome do serviço"
                  id="service"
                  name="service"
                  value={values.service}
                  onChange={handleNameService}
                  options={ALL_SERVICES_LIST?.rows}
                  optionLabel="nome"
                  errorMessage={touched.service ? errors?.service : ''}
                />
                <FormSelect
                  withBorder={true}
                  label="Categoria do serviço"
                  id="category"
                  name="category"
                  value={values.category}
                  onChange={handleCategoryService}
                  options={CATEGORY_LIST?.rows}
                  optionLabel="nome"
                  errorMessage={touched.category ? errors?.category : ''}
                />
              </CustomView>
              <CustomView
                maxWidth={967}
                mt={{ xs: '2px', md: '15px' }}
                mb="40px"
                gap={1}
                justifyContent="flex-start"
                flexDirection={{ xs: 'column', md: 'row' }}
              >
                <CustomView>
                  <InputCurrency
                    placeholder="R$"
                    id="value"
                    label="Valor orçado*"
                    name="value"
                    value={values.value * 100}
                    variant="outlined"
                    onChange={handleChange}
                    error={!!(touched?.value && errors?.value)}
                  />
                  {touched.value && errors?.value && (
                    <div style={{ color: 'red', fontSize: '12px' }}>{String(errors.value)}</div>
                  )}
                  <div />
                </CustomView>
              </CustomView>
              <CustomView>
                <CustomView borderBottom="1px solid #8F8E914D" sx={{ mb: '2rem' }}>
                  <CustomGraphy fontSize="18px">Fotos</CustomGraphy>
                </CustomView>
                <CustomView sx={{ mb: '2rem' }}>
                  {isPhotosLoading ? (
                    <GallerySkeleton />
                  ) : (
                    <BreakdownGallery
                      images={images}
                      selectedId={values.inspectionPhotoId}
                      onSelectImage={(image?: any) => {
                        setFieldValue('inspectionPhotoId', image?.id)
                        setFieldValue('inspectionPhoto', image)
                      }}
                    />
                  )}
                </CustomView>
              </CustomView>
              <CustomView flexDirection="row" alignItems="center" justifyContent="space-between">
                <CustomGraphy width="100%" as="lowOpacity">
                  * Campo de preenchimento obrigatório
                </CustomGraphy>
                <CustomView
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={8}
                >
                  <CustomButton variable="default" onClick={returnToTable}>
                    Cancelar
                  </CustomButton>
                  <CustomButton variable="bordered" type="submit">
                    {isEditing ? 'Editar' : 'Adicionar'} serviço
                  </CustomButton>
                </CustomView>
              </CustomView>
            </form>
          )}
        </CustomView>
      </CustomView>
    </Modal>
  )
}

function GallerySkeleton() {
  const array = Array.from(new Array(3))
  return (
    <Grid container wrap="nowrap" gap={2}>
      {array.map((item, index) => (
        <Skeleton key={index} variant="rectangular" width={210} height={186} />
      ))}
    </Grid>
  )
}

export default NewServicePreparation
